import { useAuth0 } from '../../../auth/AuthProvider';
import { Divider, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { FormEvent, useEffect, useState } from 'react';
import useGetDiveCertificates from '../../../hooks/useGetDiveCertificates';
import useUpsertDiveCertification from '../../../hooks/useUpsertDiveCertification';
import {
  CERTIFICATE_LEVELS,
  CERTIFYING_AGENCIES,
  normalizeCertificateLevel,
} from '../../../constants/diveCertification';
import { displayEmpty } from '../../../common/utilities/displayEmpty';
import { isDiveCertificateComplete } from '../utilities/profileCompleteness';
import ProfileRequiredBadge from '../components/ProfileRequiredBadge';
import ProfileSubpageState from '../components/ProfileSubpageState';

const emptyCertificateDetails = {
  level: '',
  agency: '',
  number: '',
};

const DiveCertificate = () => {
  const { user } = useAuth0();
  const { diveCertificates, isLoading, isError, refetch, isFetching } =
    useGetDiveCertificates(user?.sub ?? '', true);
  const upsertDiveCertification = useUpsertDiveCertification();

  const [editMode, setEditMode] = useState(false);
  const [certificateDetails, setCertificateDetails] = useState(
    emptyCertificateDetails,
  );

  const syncFromServer = () => {
    setCertificateDetails({
      level: normalizeCertificateLevel(diveCertificates?.certificateLevel),
      agency: diveCertificates?.certifyingAgency || '',
      number: diveCertificates?.certificateNumber || '',
    });
  };

  useEffect(() => {
    if (!editMode) {
      syncFromServer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diveCertificates, editMode]);

  const handleLevelChange = (event: SelectChangeEvent) => {
    setCertificateDetails(prev => ({
      ...prev,
      level: normalizeCertificateLevel(event.target.value),
    }));
  };

  const handleAgencyChange = (event: SelectChangeEvent) => {
    setCertificateDetails(prev => ({ ...prev, agency: event.target.value }));
  };

  const handleNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCertificateDetails(prev => ({
      ...prev,
      number: event.target.value,
    }));
  };

  const handleCancel = () => {
    syncFromServer();
    setEditMode(false);
  };

  const handleFormSubmit = async (event: FormEvent) => {
    event.preventDefault();
    try {
      await upsertDiveCertification.mutateAsync({
        certificateId: diveCertificates?.certificateId || -1,
        userId: user?.sub ?? '',
        certificateLevel: normalizeCertificateLevel(certificateDetails.level),
        certifyingAgency: certificateDetails.agency,
        certificateNumber: certificateDetails.number,
      });
      setEditMode(false);
    } catch {
      // Keep edit mode open; toast handled by mutation feedback.
    }
  };

  if (isLoading || isError) {
    return (
      <ProfileSubpageState
        title="Dive certificate"
        isLoading={isLoading}
        isError={isError}
        errorMessage="We couldn’t load your dive certificate. Please try again."
        onRetry={() => {
          void refetch();
        }}
        isRetrying={isFetching && isError}
      />
    );
  }

  const isDiveCertificateRequired = !isDiveCertificateComplete({
    certificateLevel: certificateDetails.level,
    certifyingAgency: certificateDetails.agency,
    certificateNumber: certificateDetails.number,
  });

  return (
    <div className="px-6 md:px-0 md:min-w-[772px]">
      <div className="flex flex-col">
        <span className="text-2xl md:text-3xl font-semibold text-neutral-800">
          Dive certificate
        </span>
        <span className="mt-2 text-base text-neutral-500">
          Provide your dive certification level and the certifying organization.
        </span>
      </div>
      <form className="mt-6" onSubmit={handleFormSubmit}>
        <div className="flex justify-between font-semibold">
          <div className="flex items-center">
            <span className="text-xl">Dive certificate</span>
            {editMode && isDiveCertificateRequired && <ProfileRequiredBadge />}
          </div>
          <button
            type="button"
            className="underline"
            onClick={() => (editMode ? handleCancel() : setEditMode(true))}
          >
            {editMode ? 'Cancel' : 'Edit'}
          </button>
        </div>
        <div className="flex flex-col mt-4">
          <span className="text-neutral-500">Certificate level</span>
          {editMode ? (
            <div className="mb-4">
              <Select
                value={certificateDetails.level}
                onChange={handleLevelChange}
                displayEmpty
                fullWidth
              >
                <MenuItem value="">
                  <em>Select dive certification level</em>
                </MenuItem>
                {CERTIFICATE_LEVELS.map(level => (
                  <MenuItem key={level} value={level}>
                    {level}
                  </MenuItem>
                ))}
              </Select>
            </div>
          ) : (
            <span className="mt-2 mb-4 text-neutral-800">
              {displayEmpty(certificateDetails.level)}
            </span>
          )}
        </div>
        <Divider />
        <div className="mt-4 flex flex-col">
          <span className="text-neutral-500">Certifying agency</span>
          {editMode ? (
            <div className="mb-4">
              <Select
                value={certificateDetails.agency}
                onChange={handleAgencyChange}
                displayEmpty
                fullWidth
              >
                <MenuItem value="">
                  <em>Select certifying agency</em>
                </MenuItem>
                {certificateDetails.agency &&
                  !(CERTIFYING_AGENCIES as readonly string[]).includes(
                    certificateDetails.agency,
                  ) && (
                    <MenuItem value={certificateDetails.agency}>
                      {certificateDetails.agency}
                    </MenuItem>
                  )}
                {CERTIFYING_AGENCIES.map(agency => (
                  <MenuItem key={agency} value={agency}>
                    {agency}
                  </MenuItem>
                ))}
              </Select>
            </div>
          ) : (
            <span className="mt-2 mb-4 text-neutral-800">
              {displayEmpty(certificateDetails.agency)}
            </span>
          )}
        </div>
        <Divider />
        <div className="mt-4 flex flex-col">
          <span className="text-neutral-500">Certificate number</span>
          {editMode ? (
            <input
              type="text"
              value={certificateDetails.number}
              onChange={handleNumberChange}
              className="p-4 mb-4 border border-neutral-100 rounded-lg text-neutral-800 w-full mt-4"
            />
          ) : (
            <span className="mt-2 mb-4 text-neutral-800">
              {displayEmpty(certificateDetails.number)}
            </span>
          )}
        </div>
        {editMode && (
          <button
            type="submit"
            className="mt-4 bg-brand-secondary text-neutral-00 w-[120px] rounded-lg px-6 py-4 disabled:cursor-not-allowed disabled:opacity-60"
            disabled={upsertDiveCertification.isPending}
          >
            {upsertDiveCertification.isPending ? 'Saving…' : 'Save'}
          </button>
        )}
      </form>
    </div>
  );
};

export default DiveCertificate;
